import { useCallback, useEffect, useRef } from 'react';

/**
 * Bare hover-intent timer pair shared by the single-instance HoverPopover and
 * the multi-anchor useKeyedHoverPopover. Owns nothing but two timeouts: callers
 * decide what "open" and "close" mean. Timers are cleared on unmount.
 */
export const useHoverIntent = () => {
  const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelOpen = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const clearAll = useCallback(() => {
    cancelOpen();
    cancelClose();
  }, [cancelOpen, cancelClose]);

  const scheduleOpen = useCallback(
    (fn: () => void, delayMs: number) => {
      cancelOpen();
      openTimerRef.current = setTimeout(() => {
        openTimerRef.current = null;
        fn();
      }, delayMs);
    },
    [cancelOpen],
  );

  const scheduleClose = useCallback(
    (fn: () => void, delayMs: number) => {
      cancelClose();
      closeTimerRef.current = setTimeout(() => {
        closeTimerRef.current = null;
        fn();
      }, delayMs);
    },
    [cancelClose],
  );

  useEffect(() => clearAll, [clearAll]);

  return {
    /** True while an open is pending (used to debounce re-entry). */
    isOpenPending: () => openTimerRef.current !== null,
    scheduleOpen,
    scheduleClose,
    cancelOpen,
    cancelClose,
    clearAll,
  };
};
